import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

/**
 * Manage-Games list card for the Event Admin page.
 * Sorts games chronologically for display (Preseason first, then Regular, then Playoff by date).
 * Purely presentational — all state and mutations live in the parent.
 */
export const ManageGamesCard = ({
  allGames,
  editingGame,
  setEditingGame,
  onEditGame,
  onCancelEdit,
  onSaveEdit,
  onDeleteGame,
  formatDate,
}) => {
  const sortedGames = [...allGames].sort((a, b) => {
    const da = (a.date || '9999-99-99');
    const db = (b.date || '9999-99-99');
    if (da !== db) return da.localeCompare(db);
    // Same date → sort by time then by game_index
    const ta = (a.timeCT || a.time_ct || '');
    const tb = (b.timeCT || b.time_ct || '');
    if (ta !== tb) return ta.localeCompare(tb);
    return (a.game_index || 0) - (b.game_index || 0);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>🎮 Manage Games ({allGames.length})</CardTitle>
        <CardDescription>View, edit and manage all season games</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="max-h-[500px] overflow-y-auto space-y-2">
          {allGames.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No games found</p>
          ) : (
            sortedGames.map((game, idx) => (
              <div key={game.game_index} data-testid={`manage-game-row-${idx}`} className="p-3 rounded-lg border bg-card">
                {editingGame && editingGame.game_index === game.game_index ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <label className="text-xs font-medium">Type</label>
                        <select
                          value={editingGame.season_type}
                          onChange={(e) => setEditingGame({ ...editingGame, season_type: e.target.value, type: e.target.value })}
                          className="mt-1 w-full p-2 rounded bg-background border border-border text-sm"
                        >
                          <option value="preseason">Preseason</option>
                          <option value="regular">Regular</option>
                          <option value="playoff">Playoff</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Date</label>
                        <Input
                          type="date"
                          value={editingGame.date}
                          onChange={(e) => setEditingGame({ ...editingGame, date: e.target.value })}
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Time</label>
                        <Input
                          value={editingGame.timeCT}
                          onChange={(e) => setEditingGame({ ...editingGame, timeCT: e.target.value })}
                          className="mt-1 h-9"
                          placeholder="7:30 PM"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Opponent</label>
                        <Input
                          value={editingGame.opponent}
                          onChange={(e) => setEditingGame({ ...editingGame, opponent: e.target.value })}
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium">Note</label>
                      <Input
                        value={editingGame.note || ''}
                        onChange={(e) => setEditingGame({ ...editingGame, note: e.target.value })}
                        className="mt-1 h-9"
                        placeholder="Optional note"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={onCancelEdit}>Cancel</Button>
                      <Button size="sm" onClick={onSaveEdit}>Save Changes</Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">#{idx + 1}</span>
                        <span className="font-medium">vs {game.opponent}</span>
                        {game.season_type === 'playoff' && <Badge variant="destructive" className="text-xs">Playoff</Badge>}
                        {game.season_type === 'preseason' && <Badge variant="secondary" className="text-xs">Preseason</Badge>}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(game.date)} at {game.timeCT}
                        {game.note && <span className="italic"> - {game.note}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => onEditGame(game)}>Edit</Button>
                      <Button variant="destructive" size="sm" onClick={() => onDeleteGame(game.game_index)}>Delete</Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ManageGamesCard;
